import ControllerBase from 'global/js/ControllerBase';
import ControllerProxy from 'global/js/ControllerProxy';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	MoleculeFinderSelectStepEvent,
	MyMoleculeFinderSelectStepEvents,
} from 'wolf/components/molecules/m-finder-select-step/m-finder-select-step.events';
import Controller from '../../../global/js/controller';
import { Component } from 'global/interfaces/component.interface';
import { MoleculeFinderSelectStepProps } from 'wolf/components/molecules/m-finder-select-step/m-finder-select-step.data';
import UIkit from 'uikit';
import { OrganismFinderProps } from './o-finder.data';
import { MoleculePaginationEvent, MyMoleculePaginationEvents } from 'wolf/components/molecules/m-pagination/m-pagination.events';
import { HttpActionResponse } from 'global/interfaces/http.interface';

const finderSelectStepTemplate = require('components/molecules/m-finder-select-step/m-finder-select-step.template.twig') as (
	data: Component<MoleculeFinderSelectStepProps>,
) => string;

interface ComponentRefs {
	selectWrapper: 'selectWrapper';
	contentWrapper: 'contentWrapper';
	paginationWrapper: 'paginationWrapper';
	contentWrapperMobile: 'contentWrapperMobile';
	popupWrapper: 'popupWrapper';
}

interface FetchRequiredData {
	additionalId?: number;
	backLink: boolean,
	href?: string
}

export default class ControllerFinder extends ControllerBase<ComponentRefs> {
	private prevUrl: string;
	private resultsUrl: string;
	private select: HTMLDivElement;
	private dataSelect: MoleculeFinderSelectStepProps;
	private onPageChange: BehaviorSubject<any>;
	private page: number;
	// HTML string/wrappers
	private desktopWrapper: HTMLDivElement;
	private paginationWrapper: HTMLDivElement;
	private mobileWrapper: HTMLDivElement;
	private popupWrapper: HTMLDivElement;
	private desktopHtml: string;
	private paginationHtml: string;
	private mobileHtml: string;
	private popupHtml: string;
	private isHubspotForm: boolean;

	constructor(element: HTMLElement) {
		super(element);

		this.select = this.$references.selectWrapper as HTMLDivElement;
		this.resultsUrl = this.select.dataset.resultsurl as string;
		this.mobileWrapper = this.$references.contentWrapperMobile as HTMLDivElement;
		this.desktopWrapper = this.$references.contentWrapper as HTMLDivElement;
		this.paginationWrapper = this.$references.paginationWrapper as HTMLDivElement;
		this.popupWrapper = this.$references.popupWrapper as HTMLDivElement;
		this.isHubspotForm = false;

		this.init();
	}

	private init(): void {
		this.onPageChange = new BehaviorSubject(null);

		this.onPageChange.subscribe((newPage) => {
			if (newPage) {
				this.loadNewContent();
			}
		});
		setTimeout(() => {
			const hubspotForm = document.querySelector('.m-finder-popup-form__content .hs-form');
			this.isHubspotForm = !!hubspotForm;
		}, 1200);

		this.observePageEvent();
		this.observePreviousEvent();
		this.observeNextEvent();
	}

	/**
	 * Listen for a change of page
	 */
	private observePageEvent() {
		fromEvent(window, MyMoleculePaginationEvents.pageEvent)
			.pipe(
				map((event: CustomEvent<MoleculePaginationEvent>) => {
					return event.detail.href;
				}),
			)
			.subscribe((e) => {
				this.fetchResults({ href: e, backLink: false })
					.then(response => {
						response && this.setData(response.data);
					})
					.catch(err => {
						console.error(err);
					})
			});
	}

	/**
	 * Listen if an answer button is being clicked
	 */
	private observeNextEvent() {
		// Event to go to the next step
		fromEvent(window, MyMoleculeFinderSelectStepEvents.clicked)
			.pipe(
				map((event: CustomEvent<MoleculeFinderSelectStepEvent>) => {
					return event.detail.additionalId;
				}),
			)
			.subscribe((e) => {
				this.fetchResults({ additionalId: e, backLink: false })
					.then((response) => {
						response && this.setData(response.data);
					})
					.catch((err) => {
						console.error(err);
					});
			});

		// Event if user is in last question to go back to the start
		fromEvent(window, MyMoleculeFinderSelectStepEvents.start)
			.pipe(
				map((event: CustomEvent<MoleculeFinderSelectStepEvent>) => {
					return event.detail.additionalId;
				}),
			)
			.subscribe((e) => {
				this.fetchResults({ additionalId: e, backLink: false })
					.then((response) => {
						response && this.setData(response.data);
					})
					.catch((err) => {
						console.error(err);
					});
			});
	}

	/**
	 * Listen if the back button is being clicked
	 */
	private observePreviousEvent(): void {
		fromEvent(window, MyMoleculeFinderSelectStepEvents.back).subscribe(() => {
			this.fetchResults({ backLink: true })
				.then((response) => {
					response && this.setData(response.data);
				})
				.catch((err) => {
					console.error(err);
				});
		});
	}
	/**
	 * @param  {} {additionalId=undefined
	 * @param  {} backLink=false
	 * @param  {FetchRequiredData} href=''}
	 * @returns Promise
	 */
	private async fetchResults({ additionalId = undefined, backLink = false, href = undefined }: FetchRequiredData): Promise<HttpActionResponse<OrganismFinderProps, any>> {
		const url = new URL(this.resultsUrl);

		if (additionalId) {
			url.searchParams.append('additionalId', additionalId.toString());
		}

		if (href) {
			const newParams = new URLSearchParams(href)

			newParams.forEach((value, key) => {
				url.searchParams.append(key, value)
			})

		}

		const response = await fetch(backLink ? this.prevUrl : url.toString());
		return await response.json();
	}

	/**
	 * Sets the data received and renders the template with new content
	 */
	private loadNewContent() {
		// Set Data
		const newSelectProps: MoleculeFinderSelectStepProps = {
			...this.dataSelect,
		};

		// Create template with data
		const selectToRender = finderSelectStepTemplate({
			data: newSelectProps,
		});

		const selectRef: ComponentRefs['selectWrapper'] = "selectWrapper";
		// Render templates
		selectToRender && this.domUtils.renderDomElement(selectRef, selectToRender);
		this.desktopWrapper.innerHTML = this.desktopHtml;
		this.paginationWrapper.innerHTML = this.paginationHtml;
		this.mobileWrapper.innerHTML = this.mobileHtml;
		if(!this.isHubspotForm) {
			this.popupWrapper.innerHTML = this.popupHtml;
		}
	}

	/**
	 * Assigns the response received in local variables and triggers the event
	 */
	private setData(response: OrganismFinderProps | undefined): void {
		if (response && response.desktopHtml && response.mobileHtml && response.popupHtml) {

			this.dataSelect = response.selectStep;
			this.prevUrl = response.previousDataUrl || '';
			// HTML strings
			this.mobileHtml = response.mobileHtml;
			this.desktopHtml = response.desktopHtml;
			this.paginationHtml = response.pagination;
			if(!this.isHubspotForm) {
				this.popupHtml = response.popupHtml;
			}
		}

		this.onPageChange.next(response);

		this.initializeNestedComponents();
		this.registerModalOpener();
	}

	protected initializeNestedComponents(): void {
		const components = this.$element.querySelectorAll('[data-controller]');

		components.forEach((element: HTMLElement) => {
			const componentName = <string>element.dataset.controller;
			new ControllerProxy(Controller, componentName, element);
		});
	}

	private registerModalOpener() {
		const modals = document.querySelectorAll("a[href^='#layout-']");

		modals &&
			modals.forEach((modal) => {
				modal.addEventListener('click', (e) => {
					e.preventDefault();

					const name = modal.getAttribute('href')?.split('#layout-')[1];
					UIkit.modal(`#${name}`).show();
				});
			});
	}
}
